import React, { useEffect, useState } from 'react';
import { ArrowRight, Clock, MapPin, Save, LogOut } from 'lucide-react';
import useAuthStore from '../../game/authStore';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const inputClass = "w-full py-3 px-4 rounded-xl text-sm bg-[#0f1a30] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30";

export default function AuthScreen({ playerAvatar, onContinue, onLoadGame }) {
  const { user, savedGames, loading, error, validateToken, logout, register, login, forgotPassword } = useAuthStore();
  const [mode, setMode] = useState('register'); // 'register' | 'login' | 'forgot'
  const [form, setForm] = useState({ displayName: '', email: '', phone: '', password: '' });
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const handleField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setForgotSent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'forgot') {
      const ok = await forgotPassword(form.email);
      if (ok) setForgotSent(true);
      return;
    }
    const ok = mode === 'register'
      ? await register(form.email, form.phone, form.password, form.displayName)
      : await login(form.email, form.password);
    if (ok) onContinue();
  };

  if (user) {
    return (
      <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] p-4 sm:p-6 min-h-[400px] sm:min-h-[600px] flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="text-center mb-4 sm:mb-6">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName}
                className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-xl object-cover border-2 border-white/10"
              />
            ) : playerAvatar ? (
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-xl overflow-hidden border-2 border-white/10"
                dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
            ) : null}
            <h2 className="text-lg sm:text-xl font-bold text-white">
              Bem-vindo, {user.displayName || 'Jogador'}!
            </h2>
            {user.provider && (
              <p className="text-xs text-gray-500 mt-1 capitalize">via {user.provider}</p>
            )}
          </div>

          {savedGames.length > 0 && (
            <div className="mb-6">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-3">
                Jogos Salvos
              </p>
              <div className="space-y-2">
                {savedGames.map(save => (
                  <button
                    key={save.id}
                    onClick={() => onLoadGame(save)}
                    className="w-full p-3 rounded-xl text-left transition-all hover:scale-[1.01]"
                    style={{ background: '#0f1a30', border: '1px solid #1e3050' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: '#162040' }}>
                        <Save size={18} className="text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-white truncate">
                            {save.cityName || 'Jogo Salvo'}
                          </p>
                          {save.stateSigla && (
                            <span className="text-[9px] text-gray-500 flex items-center gap-0.5">
                              <MapPin size={8} /> {save.stateSigla}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-[10px] text-gray-400">
                            {save.currentOffice || 'Assessor'} · Turno {save.turn || 0} · Rodada {save.round || 1}
                          </span>
                        </div>
                        <span className="text-[9px] text-gray-600 flex items-center gap-1 mt-0.5">
                          <Clock size={8} /> {formatDate(save.updatedAt)}
                        </span>
                      </div>
                      <ArrowRight size={16} className="text-gray-600 shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={onContinue}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
              style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}
            >
              {savedGames.length > 0 ? 'Novo Jogo' : 'Começar Jogo'} <ArrowRight size={16} />
            </button>

            <button
              onClick={logout}
              className="w-full py-2 rounded-xl text-xs text-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-1"
            >
              <LogOut size={12} /> Sair da conta
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] p-4 sm:p-6 min-h-[400px] sm:min-h-[600px] flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6 sm:mb-8">
          {playerAvatar && (
            <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-xl overflow-hidden border-2 border-white/10"
              dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
          )}
          <h2 className="text-lg sm:text-xl font-bold text-white">
            {mode === 'register' ? 'Criar conta local' : mode === 'login' ? 'Entrar na sua conta' : 'Recuperar senha'}
          </h2>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">
            {mode === 'forgot' ? 'Enviaremos um link de redefinição por email' : 'Salve seu progresso para continuar depois'}
          </p>
        </div>

        {mode === 'forgot' && forgotSent ? (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-xs text-emerald-300">
              Se o email <strong>{form.email}</strong> estiver cadastrado, um link de recuperação foi enviado. Verifique sua caixa de entrada.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'register' && (
              <input
                type="text"
                placeholder="Nome"
                value={form.displayName}
                onChange={handleField('displayName')}
                className={inputClass}
              />
            )}
            <input
              type="text"
              placeholder={mode === 'register' ? 'Email' : mode === 'forgot' ? 'Email' : 'Email ou telefone'}
              value={form.email}
              onChange={handleField('email')}
              required
              className={inputClass}
            />
            {mode === 'register' && (
              <input
                type="tel"
                placeholder="Telefone"
                value={form.phone}
                onChange={handleField('phone')}
                required
                className={inputClass}
              />
            )}
            {mode !== 'forgot' && (
              <input
                type="password"
                placeholder="Senha"
                value={form.password}
                onChange={handleField('password')}
                required
                minLength={4}
                className={inputClass}
              />
            )}

            {mode === 'login' && (
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="block text-xs text-gray-400 hover:text-white transition-colors"
              >
                Esqueci minha senha
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90 active:scale-[0.98]"
              style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
              ) : (
                <>
                  {mode === 'register' ? 'Criar conta' : mode === 'login' ? 'Entrar' : 'Enviar link de recuperação'}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={() => switchMode(mode === 'forgot' ? 'login' : mode === 'register' ? 'login' : 'register')}
          className="w-full mt-3 py-2 text-xs text-gray-400 hover:text-white transition-colors"
        >
          {mode === 'forgot' ? 'Voltar para login' : mode === 'register' ? 'Já tem conta? Entrar' : 'Não tem conta? Criar conta'}
        </button>

        {error && (
          <div className="mt-4 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={onContinue}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
          >
            Sem Salvar <ArrowRight size={16} />
          </button>
        </div>

        <p className="text-[10px] text-gray-600 text-center mt-4 leading-relaxed">
          Ao continuar, você concorda com nossos Termos de Uso e Política de Privacidade.
        </p>
      </div>
    </div>
  );
}
